// Reservas de zonas sociales: elegir zona → calendario → crear reserva.
// Es la misma funcionalidad de la app y debe comportarse igual:
//   coordiph/lib/widgets/modulo_reservas/listado_zonas.dart            (SelZona)
//   coordiph/lib/widgets/modulo_reservas/seleccionar_fecha_reserva.dart (Calendario)
//   coordiph/lib/widgets/modulo_reservas/crear_reserva.dart             (Formulario)
// Las reglas están en functions/reservas_reglas.js y el acceso a Firebase en functions/reservas.js.
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { zonaIdANombre, zonasActivasDelConjunto } from "../../../functions/zonas_catalogo";
import {
  MAX_INVITADOS, HORAS_INTERMEDIAS, HORAS_COMPLETAS, diferencia, parseHora, duracionTexto,
  motivoConflicto, motivoDia, motivoFechaLimite, generarDireccion, listaViviendasPersonal,
  fechaHoraDart, fechaISO, reservaAJson,
} from "../../../functions/reservas_reglas";
import {
  cargarConjunto, cargarZona, cargarReservasCalendario, cargarSolicitudesAprobadas, validarAforo,
  motivosCuota, incrementarContadores, cargarUsuario, crearReserva, escribirIndices, anularReserva,
  crearPaseVisitante, enlazarPase, obtenerIdConjunto,
} from "../../../functions/reservas";
import { subirArchivoFirebase } from "../../../functions/archivos_firebase";
import BarraSuperior from "../barra_superior";
import BotonSOS from "../sos/boton_sos";

const R = "#c0392b", RB = "#fff5f5", RBo = "#f5c6c6";
const T = "#1a1a1a", G = "#6b7280", Bo = "#e5e7eb", F = "#ffffff";

// Colores por estado, los mismos del calendario de la app.
const EC = {
  Autorizado: "#118524",
  Pendiente: "#D45A22",
  Rechazado: "#C2342A",
  Anulado: "#4E4E4E",
};
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

const sesion = () => ({
  codigo: localStorage.getItem("CodigoCliente") || "",
  correo: localStorage.getItem("CorreoElectronico") || "",
  permisos: localStorage.getItem("Permisos") || "",
  nombreCompleto: localStorage.getItem("NombreCompleto") || "",
  residencia: localStorage.getItem("Direccion") || "",
  razonSocial: localStorage.getItem("RazonSocial") || "",
});
const esAdminRol = (p) => p === "ADMINISTRADOR" || p === "SUPER";
const esStaffRol = (p) => p === "GUARDA" || esAdminRol(p);

// Mensaje corto como el "notificacionFlotante" de la app.
const avisar = (texto) => Swal.fire({ text: texto, confirmButtonColor: R, confirmButtonText: "Entendido" });

const ctrl = { padding: "12px 14px", border: `1.5px solid ${Bo}`, borderRadius: "8px", fontSize: "16px", color: T, background: F, outline: "none", width: "100%", boxSizing: "border-box" };
const Lbl = ({ c }) => <label style={{ fontSize: "16px", fontWeight: "700", color: T }}>{c}</label>;
const Fld = ({ children }) => <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>{children}</div>;
const Box = ({ title, children }) => <div style={{ background: F, border: `1.5px solid ${Bo}`, borderRadius: "12px", padding: "18px 20px", marginBottom: "14px" }}>{title && <><p style={{ fontSize: "17px", fontWeight: "700", color: T, margin: "0 0 10px" }}>{title}</p><hr style={{ border: "none", borderTop: `1px solid ${Bo}`, margin: "0 0 14px" }} /></>}{children}</div>;
const Spin = () => <div style={{ width: 28, height: 28, border: `3px solid ${Bo}`, borderTopColor: R, borderRadius: "50%", animation: "spin .8s linear infinite" }}><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;
const Load = ({ msg = "Cargando..." }) => <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 0", gap: "12px" }}><Spin /><p style={{ color: G, fontSize: "16px", margin: 0 }}>{msg}</p></div>;
const Back = ({ onClick }) => <button style={{ padding: "10px 18px", background: "transparent", border: `1.5px solid ${Bo}`, borderRadius: "8px", cursor: "pointer", fontSize: "16px", color: T, fontWeight: "500" }} onClick={onClick}>← Volver</button>;
const BotonRojo = ({ children, style, ...p }) => <button style={{ padding: "12px 20px", background: R, color: "#fff", border: "none", borderRadius: "10px", cursor: "pointer", fontSize: "16px", fontWeight: "700", ...style }} {...p}>{children}</button>;

// ───────────────────────── 1. Elegir la zona (listado_zonas.dart) ─────────────────────────
const SelZona = ({ onSelect }) => {
  const navigate = useNavigate();
  const { codigo, permisos } = sesion();
  const [zonas, setZonas] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    cargarConjunto(codigo)
      .then(conjunto => setZonas(zonasActivasDelConjunto(conjunto)))
      .catch(console.error)
      .finally(() => setCargando(false));
  }, [codigo]);

  if (cargando) return <Load msg="Cargando zonas..." />;
  return (
    <div style={{ padding: "28px 16px", maxWidth: "960px", margin: "0 auto" }}>
      <div style={{ marginBottom: "28px", borderBottom: `2px solid ${RBo}`, paddingBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontSize: "24px", fontWeight: "700", color: T, margin: "0 0 4px" }}>Elige la zona social</h2>
          <p style={{ fontSize: "16px", color: G, margin: 0 }}>Mis zonas sociales</p>
        </div>
        {esAdminRol(permisos) && <BotonRojo onClick={() => navigate("/estadistica")}>Consultar estadística</BotonRojo>}
      </div>
      {!zonas.length
        ? <p style={{ textAlign: "center", padding: "60px 0", color: "#9ca3af", fontSize: "16px" }}>No hay zonas sociales habilitadas.</p>
        : <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: "16px" }}>
          {zonas.map(z => (
            <button key={z.id} onClick={() => onSelect({ zonaId: z.id, titulo: z.titulo })}
              style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 12px", background: F, border: `1.5px solid ${Bo}`, borderRadius: "12px", cursor: "pointer", gap: "10px", minHeight: "140px", boxShadow: "0 1px 4px rgba(0,0,0,.06)" }}>
              <span style={{ fontSize: "40px" }}>{z.icono}</span>
              <span style={{ fontSize: "16px", fontWeight: "700", color: T, textAlign: "center" }}>{z.titulo}</span>
            </button>
          ))}
        </div>}
    </div>
  );
};

// ───────────────────── 2. Calendario de la zona (seleccionar_fecha_reserva.dart) ─────────────────────
const hora12 = (h) => {
  const [hh, mm] = String(h).split(":").map(Number);
  return `${String(hh % 12 || 12).padStart(2, "0")}:${String(mm).padStart(2, "0")} ${hh < 12 ? "AM" : "PM"}`;
};
const nombreSede = (r) => (!r.sedeSeleccionada || r.sedeSeleccionada === "null" || r.sedeSeleccionada === "0" ? "1" : r.sedeSeleccionada);

const Calendario = ({ zona, onCrear, onBack }) => {
  const { codigo, correo, permisos } = sesion();
  const [reservas, setReservas] = useState([]);
  const [permisosZona, setPermisosZona] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [mes, setMes] = useState(new Date());
  const [sel, setSel] = useState(fechaISO(new Date()));

  const cargarDatos = useCallback(async () => {
    setCargando(true);
    try {
      const [p, r] = await Promise.all([cargarZona(codigo, zona.zonaId), cargarReservasCalendario(codigo, zona.zonaId)]);
      setPermisosZona(p);
      setReservas(r.filter(x => x.fecha && x.fecha !== "null"));
    } catch (e) { console.error(e); }
    setCargando(false);
  }, [codigo, zona.zonaId]);
  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const nombreCita = (r) => {
    const sede = nombreSede(r);
    const casa = (r.casa ?? "").toUpperCase();
    if (permisos === "ADMINISTRADOR" || permisos === "SUPER" || permisos === "GUARDA") {
      const t = r.tituloReserva;
      return t && t !== "null" && t !== "NO APLICA" ? `SEDE ${sede} - ${casa} (${t})` : `SEDE ${sede} - ${casa}`;
    }
    return r.usuario === correo ? `SEDE ${sede} - ${casa}` : "USUARIO DE LA COPROPIEDAD";
  };
  const puedeAnular = (r) => {
    if (r.estado === "Anulado" || r.estado === "Rechazado") return false;
    if (esAdminRol(permisos)) return true;
    return permisos === "RESIDENTE" && r.usuario === correo;
  };

  const anular = async (r) => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Anular Reserva?",
      html: `<p><strong>Fecha:</strong> ${r.fecha}</p><p>Casa: ${r.casa ?? ""}</p><p>Estado: ${r.estado ?? ""}</p>`,
      showCancelButton: true, confirmButtonText: "Anular", cancelButtonText: "Cancelar", confirmButtonColor: "#dc2626",
    });
    if (!isConfirmed) return;
    setCargando(true);
    const ok = await anularReserva(codigo, r);
    await avisar(ok ? "Reserva anulada" : "Error al anular");
    await cargarDatos();
  };

  const verDetalle = (r) => {
    const [y, m, d] = r.fecha.split("-");
    const hora = r.horaInicio && r.horaFinal ? `${hora12(r.horaInicio)} - ${hora12(r.horaFinal)}` : "Todo el día";
    const detalles = permisosZona?.agregarDetalles ?? false;
    const fila = (titulo, texto, color = T) => `<p style="margin:10px 0 0;color:#6b7280;font-size:14px">${titulo}</p><p style="margin:0;font-weight:600;color:${color};font-size:16px">${texto}</p>`;
    Swal.fire({
      title: nombreCita(r),
      html: `<div style="text-align:left;font-size:16px">
        <p style="margin:4px 0">📅 ${d}/${m}/${y}</p>
        <p style="margin:4px 0">🕒 ${hora}</p>
        <p style="margin:4px 0"><span style="color:${EC[r.estado] ?? "#9e9e9e"}">●</span> ${r.estado ?? ""}</p>
        ${detalles && r.tituloReserva ? fila("Título:", r.tituloReserva) : ""}
        ${detalles && r.descripcionReserva ? fila("Descripción:", r.descripcionReserva) : ""}
        ${r.estado === "Rechazado" && r.motivoRechazo ? fila("Motivo rechazo:", r.motivoRechazo, "#dc2626") : ""}
      </div>`,
      confirmButtonText: "Cerrar", confirmButtonColor: R,
    });
  };

  const anio = mes.getFullYear(), m = mes.getMonth();
  const fStr = d => `${anio}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  const porFecha = reservas.reduce((a, r) => { (a[r.fecha] ??= []).push(r); return a; }, {});
  const delDia = (porFecha[sel] ?? []).slice().sort((a, b) => String(a.horaInicio).localeCompare(String(b.horaInicio)));
  const celdas = [...Array(new Date(anio, m, 1).getDay()).fill(null), ...Array.from({ length: new Date(anio, m + 1, 0).getDate() }, (_, i) => i + 1)];
  while (celdas.length % 7 !== 0) celdas.push(null);
  const hoyStr = fechaISO(new Date());

  // La app: el residente ve "Nueva Reserva"; administrador y súper ven "Crear reserva (fecha)"; los demás no crean.
  const botonCrear = permisos === "RESIDENTE"
    ? <BotonRojo onClick={() => onCrear(sel)}>+ Nueva Reserva</BotonRojo>
    : esAdminRol(permisos) ? <BotonRojo onClick={() => onCrear(sel)}>Crear reserva ({sel})</BotonRojo> : null;

  const card = { background: F, border: `1.5px solid ${Bo}`, borderRadius: "14px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,.05)" };
  return (
    <div style={{ padding: "20px 16px", maxWidth: "1100px", margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px", paddingBottom: "16px", borderBottom: `2px solid ${RBo}`, gap: "12px", flexWrap: "wrap" }}>
        <Back onClick={onBack} />
        <h2 style={{ fontSize: "22px", fontWeight: "700", color: T, margin: 0 }}>Reservar {zona.titulo}</h2>
        <BotonRojo onClick={cargarDatos} disabled={cargando}>{cargando ? "Cargando..." : "Actualizar"}</BotonRojo>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: "24px", alignItems: "start" }}>
        <div style={card}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
            <button style={{ background: "#f3f4f6", border: `1.5px solid ${Bo}`, borderRadius: "8px", width: "44px", height: "44px", cursor: "pointer", fontSize: "22px" }} onClick={() => setMes(new Date(anio, m - 1, 1))}>‹</button>
            <span style={{ fontSize: "19px", fontWeight: "700", color: T }}>{MESES[m]} {anio}</span>
            <button style={{ background: "#f3f4f6", border: `1.5px solid ${Bo}`, borderRadius: "8px", width: "44px", height: "44px", cursor: "pointer", fontSize: "22px" }} onClick={() => setMes(new Date(anio, m + 1, 1))}>›</button>
          </div>
          {cargando ? <Load msg="Cargando reservas..." /> : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: "4px" }}>
              {DS.map(d => <div key={d} style={{ textAlign: "center", fontSize: "13px", fontWeight: "600", color: G, padding: "4px 0 8px" }}>{d}</div>)}
              {celdas.map((d, i) => {
                if (!d) return <div key={`v-${i}`} />;
                const fs = fStr(d), esSel = sel === fs, esHoy = fs === hoyStr;
                return (
                  <div key={d} onClick={() => setSel(fs)}
                    style={{ aspectRatio: "1", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", borderRadius: "8px", cursor: "pointer", border: `1.5px solid ${esSel ? R : esHoy ? RBo : "transparent"}`, background: esSel ? R : esHoy ? RB : "transparent", minHeight: "44px", gap: "2px" }}>
                    <span style={{ fontSize: "16px", fontWeight: "600", color: esSel ? "#fff" : T }}>{d}</span>
                    {(porFecha[fs] ?? []).length > 0 && <div style={{ display: "flex", gap: "2px" }}>{porFecha[fs].slice(0, 3).map((r, j) => <span key={j} style={{ width: "6px", height: "6px", borderRadius: "50%", background: esSel ? "#fff" : EC[r.estado] ?? "#9e9e9e" }} />)}</div>}
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginTop: "16px", paddingTop: "12px", borderTop: `1px solid ${Bo}` }}>
            {Object.entries(EC).map(([e, c]) => <span key={e} style={{ fontSize: "14px", color: G }}><span style={{ color: c }}>●</span> {e}</span>)}
          </div>
        </div>
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", paddingBottom: "12px", borderBottom: `1px solid ${Bo}`, gap: "12px", flexWrap: "wrap" }}>
            <div>
              <h3 style={{ fontSize: "19px", fontWeight: "700", color: T, margin: "0 0 2px" }}>{sel}</h3>
              <p style={{ fontSize: "15px", color: G, margin: 0 }}>{delDia.length === 0 ? "Sin reservas" : `${delDia.length} reserva(s)`}</p>
            </div>
            {botonCrear}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px", maxHeight: "520px", overflowY: "auto" }}>
            {delDia.length === 0
              ? <p style={{ textAlign: "center", padding: "32px 0", color: "#9ca3af", fontSize: "16px" }}>No hay reservas para este día.</p>
              : delDia.map(r => (
                <div key={r.id} style={{ display: "flex", alignItems: "center", gap: "8px", borderRadius: "8px", padding: "10px 12px", background: EC[r.estado] ?? "#9e9e9e", color: "#fff" }}>
                  <div style={{ flex: 1, cursor: "pointer" }} onClick={() => verDetalle(r)}>
                    <div style={{ fontSize: "15px", fontWeight: "700" }}>{nombreCita(r)}</div>
                    <div style={{ fontSize: "15px", opacity: .9 }}>{r.horaInicio} - {r.horaFinal}</div>
                  </div>
                  {puedeAnular(r) && <button title="Anular reserva" onClick={() => anular(r)} style={{ background: "rgba(255,255,255,.3)", color: "#fff", border: "none", borderRadius: "6px", padding: "8px 12px", cursor: "pointer", fontSize: "15px", fontWeight: 700 }}>✕ Anular</button>}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ───────────────────────── 3. Crear la reserva (crear_reserva.dart) ─────────────────────────
const vacioInvitados = () => Array.from({ length: MAX_INVITADOS }, () => ({ nombre: "", documento: "" }));

const Formulario = ({ zona, fecha, onBack, onGuardado }) => {
  const s = sesion();
  const esStaff = esStaffRol(s.permisos), esAdmin = esAdminRol(s.permisos);

  const [cargando, setCargando] = useState(true);
  const [permisosZona, setPermisosZona] = useState(null);
  const [horas, setHoras] = useState(HORAS_INTERMEDIAS);
  const [horaInicio, setHoraInicio] = useState("");
  const [horaFinal, setHoraFinal] = useState("");
  const [duracion, setDuracion] = useState(0);
  const [sede, setSede] = useState("1");
  const [invitados, setInvitados] = useState(0);
  const [registrarPases, setRegistrarPases] = useState(true);
  const [datosInv, setDatosInv] = useState(vacioInvitados);
  const [errores, setErrores] = useState({});
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [foto, setFoto] = useState(null);
  const [vistaFoto, setVistaFoto] = useState(null);
  const fotoRef = useRef(null);
  // Vivienda (solo personal): lista de la app o dirección escrita a mano.
  const [vivienda, setVivienda] = useState("ADICIONAR MANUALMENTE");
  const [tipoCasa, setTipoCasa] = useState("APARTAMENTO");
  const [sector, setSector] = useState("");
  const [numCasa, setNumCasa] = useState("");
  const [direccion, setDireccion] = useState("");
  // La app guarda en reserva.casa la dirección generada (se usa para el nombre del recibo).
  const casaReserva = useRef(s.residencia);

  const cerrarSinHorario = useCallback(async () => {
    await avisar("No existen reservas disponibles para esta fecha.");
    onBack();
  }, [onBack]);

  // cargarPermisos() + cargarPermisosZona() + obtenerAforoZona()
  useEffect(() => {
    (async () => {
      const p = await cargarZona(s.codigo, zona.zonaId);
      if (!p) { await avisar("Esta zona social tiene un horario inválido. Contacte a la administración."); onBack(); return; }
      const determinado = p.horaCompleta ?? false ? HORAS_COMPLETAS : HORAS_INTERMEDIAS;
      // La app busca el horario de la zona en la lista de medias horas.
      const iIni = HORAS_INTERMEDIAS.indexOf(p.HoraInicio), iFin = HORAS_INTERMEDIAS.indexOf(p.HoraFinal);
      if (iIni === -1 || iFin === -1 || iFin <= iIni) {
        await avisar("Esta zona social tiene un horario inválido. Contacte a la administración.");
        onBack();
        return;
      }
      let rango = HORAS_INTERMEDIAS.slice(iIni, iFin + 1);
      let ini = rango[0], fin = rango[rango.length - 1];
      if (p.reservaUnica === true) {
        const { libres } = await validarAforo(s.codigo, zona.zonaId, fecha, rango, "1");
        if (libres.length) { rango = libres; ini = libres[0]; fin = libres[libres.length - 1]; }
      }
      const d = diferencia(ini, fin);
      if (d === null) { await cerrarSinHorario(); return; }
      // Como en la app: la lista final es todo el día y, si la hora no está bloqueada,
      // la hora de inicio pasa a la próxima hora en punto (la duración no se recalcula).
      if (p.bloquearHora === false) {
        const unaHora = new Date(Date.now() + 60 * 60 * 1000);
        ini = `${String(unaHora.getHours()).padStart(2, "0")}:00`;
      }
      setPermisosZona(p);
      setHoras(determinado);
      setHoraInicio(ini);
      setHoraFinal(fin);
      setDuracion(d);
      setCargando(false);
    })().catch(e => { console.error(e); onBack(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maxInvitados = (() => { const t = permisosZona?.LimitePersonas ?? 0; return t > 0 ? t - 1 : 0; })();
  const limiteHoras = permisosZona?.LimiteHoras;

  const cambiarHora = (tipo, valor) => {
    const d = tipo === "inicio" ? diferencia(valor, horaFinal) : diferencia(horaInicio, valor);
    if (d === null) { cerrarSinHorario(); return; }
    setDuracion(d);
    tipo === "inicio" ? setHoraInicio(valor) : setHoraFinal(valor);
  };

  const actualizarDireccion = (tc, sec, cs) => {
    const dir = generarDireccion(tc, sec, cs);
    setDireccion(dir);
    casaReserva.current = dir;
  };

  const dialogoIncumplimientos = async (motivos) => {
    const { isConfirmed } = await Swal.fire({
      icon: "warning",
      title: "Esta reserva no cumple las reglas",
      html: `<div style="text-align:left;font-size:16px"><p>Revisa los siguientes puntos antes de continuar:</p>
        ${motivos.map(m => `<p>•  ${m}</p>`).join("")}
        <p style="font-style:italic;color:#555;font-size:14px">Como administrador puedes crearla de todos modos. Si no estás seguro, cancela y revisa la configuración de la zona.</p></div>`,
      showCancelButton: true, confirmButtonText: "Crear de todos modos", cancelButtonText: "Cancelar",
      confirmButtonColor: R, allowOutsideClick: false,
    });
    return isConfirmed;
  };

  // _motivoConflicto(): aprobados (+ pendientes si contarPendientes) de la misma fecha, zona y sede.
  const buscarConflicto = async (startHour, endHour, opciones = {}) => {
    if (permisosZona.reservaUnica !== true) return null;
    const listado = await cargarSolicitudesAprobadas(s.codigo, sede, "aprobados");
    if (permisosZona.contarPendientes === true) listado.push(...await cargarSolicitudesAprobadas(s.codigo, sede, "pendientes"));
    return motivoConflicto({ listado, fecha, zonaId: zona.zonaId, sede, startHour, endHour, proximaReservaDescanso: permisosZona.proximaReservaDescanso, ...opciones });
  };

  // validarCantidadPasesReservas(): un pase TEMPORAL por cada invitado con nombre.
  const crearPases = async (reserva) => {
    const lista = [];
    for (let i = 1; i <= MAX_INVITADOS; i++) {
      const nombre = reserva[`nombreInvitado${i}`];
      if (nombre && String(nombre).trim()) lista.push({ nombre, tipo: reserva[`tipoDocumentoInvitado${i}`], numero: reserva[`numeroDocumentoInvitado${i}`] });
    }
    if (!lista.length) return;
    const [year, mes] = reserva.fecha.split("-");
    for (let i = 0; i < lista.length; i++) {
      const inv = lista[i];
      const pase = {
        idReserva: reserva.id, usuario: s.nombreCompleto, fechaCreacionRegistro: fechaISO(new Date()),
        casa: s.residencia, nombreCliente: s.razonSocial, codigoCliente: s.codigo, estado: "Pendiente",
        tipoPermiso: "TEMPORAL", nombreDocumentoVisitante: inv.nombre, tipoDocumentoVisitante: inv.tipo,
        numeroDocumentoVisitante: inv.numero, marcaVehiculo: "", tipoVehiculo: "...", placasVehiculo: "",
        colorVehiculo: "...", existeVehiculo: false, fechaTemporalPermiso: reserva.fecha, fechaInicioPermiso: "",
        fechaFinalPermiso: "", horaTemporalPermiso: reserva.horaInicio, horaSalidaPermiso: reserva.horaFinal,
        eps: "", arl: "", dispositivo: "", marca: "", serial: "", tipoIngreso: "", correoNotificacion: "",
      };
      const idPase = await crearPaseVisitante(s.codigo, pase, year, mes, "TEMPORAL");
      await enlazarPase(reserva.zonaId, year, mes, reserva.id, i, idPase);
      await new Promise(r => setTimeout(r, 1000));
    }
  };

  // Validación del formulario: nombre del invitado (mín. 3 letras) y número de documento.
  const validarFormulario = () => {
    const e = {};
    for (let i = 0; i < invitados; i++) {
      if (datosInv[i].nombre.length < 3) e[`n${i}`] = `Ingrese Nombre del invitado ${i + 1}`;
      if (!datosInv[i].documento) e[`d${i}`] = `Ingrese Número de documento ${i + 1}`;
    }
    setErrores(e);
    return Object.keys(e).length === 0;
  };

  // guardarReserva()
  const guardar = async () => {
    if (!validarFormulario()) return;
    const startHour = parseHora(horaInicio), endHour = parseHora(horaFinal);
    const hIni = parseInt(horaInicio.substring(0, 2), 10), hFin = parseInt(horaFinal.substring(0, 2), 10);
    const pIni = parseInt(String(permisosZona.HoraInicio).substring(0, 2), 10);
    const pFin = parseInt(String(permisosZona.HoraFinal).substring(0, 2), 10);

    if (endHour <= startHour) { avisar("La hora final debe ser posterior a la hora inicial. No se permiten reservas que crucen la medianoche."); return; }
    if (duracion <= 0) { avisar("Debe seleccionar un rango mayor o igual a uno (1)"); return; }
    if (hIni < pIni || hFin > pFin) { avisar(`El horario elegido está fuera del rango permitido de la zona (${permisosZona.HoraInicio} a ${permisosZona.HoraFinal}).`); return; }
    if (duracion > (limiteHoras ?? 0)) { avisar(`Supera el máximo de horas permitidas por reserva (${limiteHoras}).`); return; }
    if (esStaff && vivienda === "ADICIONAR MANUALMENTE" && direccion.trim() === "") { avisar("Debe seleccionar una vivienda o ingresar la dirección manualmente."); return; }

    setCargando(true);
    const motivos = [];
    const conflicto = await buscarConflicto(startHour, endHour);
    if (conflicto) motivos.push(conflicto);
    const dia = motivoDia(fecha, permisosZona);
    if (dia) motivos.push(dia);
    motivos.push(...await motivosCuota(s.codigo, s.correo, zona.zonaId, fecha, permisosZona));
    const limite = motivoFechaLimite(fecha, permisosZona);
    if (limite) motivos.push(limite);
    const usuario = await cargarUsuario(s.correo);
    if (usuario?.estadoMora === "Activo") motivos.push(`El usuario está bloqueado por mora. La administración informa: ${usuario.comentarioMora}`);

    if (motivos.length) {
      if (esAdmin) {
        setCargando(false);
        if (!(await dialogoIncumplimientos(motivos))) return;
        setCargando(true);
      } else {
        setCargando(false);
        avisar(motivos[0]);
        return;
      }
    }

    let urlPago;
    if (foto) {
      try { urlPago = await subirArchivoFirebase(`depositos/${s.codigo}/${casaReserva.current}${fechaHoraDart()}.png`, foto); }
      catch { urlPago = null; }
    }

    const reserva = {
      casa: esStaff ? (vivienda === "ADICIONAR MANUALMENTE" ? direccion : vivienda) : s.residencia,
      titular: s.nombreCompleto,
      tituloReserva: titulo, descripcionReserva: descripcion,
      horaInicio, horaFinal,
      urlPago,
      autorizado: !permisosZona.RequiereAutorizacion,
      estado: permisosZona.RequiereAutorizacion ? "Pendiente" : "Autorizado",
      zona: zonaIdANombre[zona.zonaId],
      zonaId: zona.zonaId, usuario: s.correo, fecha, cantidadHorasReserva: duracion, correo: s.correo,
      cantidadInvitados: invitados, sedeSeleccionada: sede,
    };
    for (let i = 1; i <= MAX_INVITADOS; i++) reserva[`tipoDocumentoInvitado${i}`] = "CEDULA";
    for (let i = 0; i < invitados; i++) {
      reserva[`nombreInvitado${i + 1}`] = datosInv[i].nombre;
      reserva[`numeroDocumentoInvitado${i + 1}`] = datosInv[i].documento;
    }
    // validarDatosTituloReserva()
    if (permisosZona.agregarDetalles === true) {
      if (!reserva.tituloReserva || reserva.tituloReserva === "null") reserva.tituloReserva = "No aplica";
      if (!reserva.descripcionReserva || reserva.descripcionReserva === "null") reserva.descripcionReserva = "No aplica";
    }

    try {
      const idConjunto = await obtenerIdConjunto(s.codigo);
      await crearReserva({ codigo: s.codigo, correoSesion: s.correo, idConjunto, reserva });

      // El cupo solo se descuenta al residente y una vez guardada la reserva.
      if (!esStaff) await incrementarContadores(s.codigo, s.correo, zona.zonaId, fecha, permisosZona);

      // Otro usuario pudo reservar el mismo horario al mismo tiempo: si su reserva quedó
      // guardada antes que la mía, se anula esta.
      const choque = await buscarConflicto(startHour, endHour, { soloAnteriores: true, propia: reserva });
      if (choque) {
        await anularReserva(s.codigo, reserva);
        setCargando(false);
        avisar("Ese horario acaba de ser reservado por otra persona. Elija otro horario.");
        return;
      }

      await escribirIndices(s.codigo, reserva, reserva.estado === "Autorizado" ? "aprobados" : "pendientes");
      if (invitados > 0 && registrarPases) crearPases(reservaAJson(reserva)).catch(console.error);

      await avisar("Reserva creada correctamente.");
      onGuardado();
    } catch (e) {
      console.error("Error al crear la reserva:", e);
      avisar("No se pudo crear la reserva. Intente nuevamente.");
      setCargando(false);
    }
  };

  if (cargando || !permisosZona) return <Load msg="Cargando..." />;

  const cantidadSedes = parseInt(permisosZona.cantidadSedes, 10) || 0;
  const sedes = Array.from({ length: cantidadSedes }, (_, i) => String(i + 1));
  const opcionesHoras = horas.includes(horaInicio) ? horas : [horaInicio, ...horas];
  const opcionesFinal = horas.includes(horaFinal) ? horas : [horaFinal, ...horas];
  const bloqueo = permisosZona.bloquearHora === true;
  const duracionMal = duracion > (limiteHoras ?? 0) || duracion <= 0;
  const textoCobro = (permisosZona.DepositoTexto ?? "").trim() || "Esta zona tiene un costo por deposito:";
  const errorTxt = (k) => errores[k] && <span style={{ color: "#dc2626", fontSize: "14px" }}>{errores[k]}</span>;
  const botonCantidad = (txt, onClick) => <button onClick={onClick} style={{ width: "56px", height: "56px", borderRadius: "50%", background: "#961819", color: "#fff", border: "none", fontSize: "28px", cursor: "pointer" }}>{txt}</button>;

  return (
    <div style={{ padding: "20px 16px", maxWidth: "720px", margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px", paddingBottom: "16px", borderBottom: `2px solid ${RBo}` }}>
        <Back onClick={onBack} />
        <h2 style={{ fontSize: "22px", fontWeight: "700", color: T, margin: 0 }}>Crear Reserva</h2>
      </div>

      <h3 style={{ textAlign: "center", fontSize: "22px", fontWeight: 700, margin: "0 0 14px" }}>{zona.titulo}</h3>

      {esStaff && <Box title="Dirección de la vivienda">
        <Fld>
          <input list="viviendas-reserva" style={ctrl} value={vivienda} onChange={e => setVivienda(e.target.value)} placeholder="Agregue el destino del visitante." />
          <datalist id="viviendas-reserva">{listaViviendasPersonal(s.codigo).map(v => <option key={v} value={v} />)}</datalist>
        </Fld>
        {vivienda === "ADICIONAR MANUALMENTE" && s.permisos !== "RESIDENTE" && s.permisos !== "EMPLEADO" && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "12px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: "10px" }}>
              <Fld><Lbl c="Tipo de vivienda" />
                <select style={ctrl} value={tipoCasa} onChange={e => { setTipoCasa(e.target.value); actualizarDireccion(e.target.value, sector, numCasa); }}>
                  <option value="APARTAMENTO">APARTAMENTO</option><option value="CASA">CASA</option>
                </select></Fld>
              <Fld><Lbl c="No Torre/Etapa" /><input style={ctrl} value={sector} onChange={e => { setSector(e.target.value); actualizarDireccion(tipoCasa, e.target.value, numCasa); }} /></Fld>
              <Fld><Lbl c="No Casa" /><input style={ctrl} value={numCasa} onChange={e => { setNumCasa(e.target.value); actualizarDireccion(tipoCasa, sector, e.target.value); }} /></Fld>
            </div>
            <Fld><Lbl c="Dirección" /><input style={{ ...ctrl, background: "#d3d3d3" }} value={direccion} readOnly /></Fld>
          </div>
        )}
      </Box>}

      <Box>
        <Fld><Lbl c="Fecha seleccionada:" /><input style={{ ...ctrl, textAlign: "center", background: "#f9fafb" }} value={fecha} disabled /></Fld>
        <p style={{ fontSize: "16px", margin: "14px 0 6px" }}>Nota: Esta zona social puede ser reservada desde las {permisosZona.HoraInicio} hasta las {permisosZona.HoraFinal}</p>
        <p style={{ fontSize: "16px", margin: 0 }}>Máximo de horas permitidas por reserva: <strong>{String(limiteHoras)}</strong></p>
      </Box>

      <Box>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: "12px" }}>
          <Fld><Lbl c="Sede:" /><select style={ctrl} value={sede} onChange={e => setSede(e.target.value)}>{(sedes.includes(sede) ? sedes : [sede, ...sedes]).map(v => <option key={v} value={v}>{v}</option>)}</select></Fld>
          <Fld><Lbl c="Hora Inicio:" /><select style={ctrl} value={horaInicio} disabled={bloqueo} onChange={e => cambiarHora("inicio", e.target.value)}>{opcionesHoras.map(h => <option key={h} value={h}>{h}</option>)}</select></Fld>
          <Fld><Lbl c="Hora Final:" /><select style={ctrl} value={horaFinal} disabled={bloqueo} onChange={e => cambiarHora("final", e.target.value)}>{opcionesFinal.map(h => <option key={h} value={h}>{h}</option>)}</select></Fld>
        </div>
        <p style={{ fontSize: "16px", fontWeight: 700, margin: "16px 0 4px", textAlign: "center" }}>Total horas de reserva:</p>
        <p style={{ fontSize: "22px", fontWeight: 700, margin: 0, textAlign: "center", color: duracionMal ? R : "#16a34a" }}>{duracionTexto(duracion)}</p>
      </Box>

      {permisosZona.agregarDetalles === true && <Box title="Información adicional:">
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <Fld><Lbl c="Titulo" /><input style={ctrl} value={titulo} onChange={e => setTitulo(e.target.value)} /></Fld>
          <Fld><Lbl c="Descripcion" /><textarea style={{ ...ctrl, minHeight: "90px", resize: "vertical" }} value={descripcion} onChange={e => setDescripcion(e.target.value)} /></Fld>
        </div>
      </Box>}

      {permisosZona.DepositoBool === true && <Box>
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "10px" }}>
          <p style={{ fontSize: "17px", fontWeight: 700, margin: 0 }}>{textoCobro}</p>
          <p style={{ fontSize: "17px", fontWeight: 700, margin: 0 }}>COP: {String(permisosZona.DepositoValor)}</p>
          {vistaFoto
            ? <img src={vistaFoto} alt="Recibo de pago" style={{ width: 140, height: 140, objectFit: "cover", borderRadius: 8 }} />
            : <div style={{ width: 140, height: 140, background: "#f3f4f6", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>Sin imagen</div>}
          <BotonRojo onClick={() => fotoRef.current?.click()}>💳 Adjuntar recibo de pago</BotonRojo>
          <input ref={fotoRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files[0]; if (f) { setFoto(f); setVistaFoto(URL.createObjectURL(f)); } }} />
        </div>
      </Box>}

      {permisosZona.RequiereAutorizacion === true && <Box title="Autorización de la reserva: ">
        <p style={{ fontSize: "16px", margin: 0 }}>Esta reserva está sujeta a aprobación por parte de la administración, se le informará el estado de esta en su correo electrónico</p>
      </Box>}

      {maxInvitados > 0 && <Box title={`Invitados (Máximo ${maxInvitados}): `}>
        <p style={{ fontSize: "16px", margin: "0 0 8px", textAlign: "center" }}>Cantidad de invitados: </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", justifyContent: "center" }}>
          {Array.from({ length: maxInvitados + 1 }, (_, n) => (
            <button key={n} onClick={() => setInvitados(n)} style={{ minWidth: 48, height: 48, borderRadius: 8, border: `1.5px solid ${n === invitados ? R : Bo}`, background: n === invitados ? R : F, color: n === invitados ? "#fff" : T, fontSize: "18px", fontWeight: 700, cursor: "pointer" }}>{n}</button>
          ))}
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "14px", fontSize: "16px", cursor: "pointer" }}>
          <span style={{ flex: 1, textAlign: "center" }}>¿Crear pases con los datos de los invitados registrados?</span>
          <input type="checkbox" checked={registrarPases} onChange={e => setRegistrarPases(e.target.checked)} style={{ width: 24, height: 24 }} />
        </label>
        {permisosZona.solicitarDatosVisitantes === true && <div style={{ display: "flex", gap: "12px", justifyContent: "center", marginTop: "12px" }}>
          {invitados < maxInvitados && botonCantidad("+", () => setInvitados(n => Math.min(maxInvitados, n + 1)))}
          {invitados > 0 && botonCantidad("-", () => setInvitados(n => Math.max(0, n - 1)))}
        </div>}
      </Box>}

      {invitados > 0 && <Box>
        {Array.from({ length: invitados }, (_, i) => (
          <div key={i} style={{ display: "flex", flexDirection: "column", gap: "8px", paddingBottom: "12px", marginBottom: "12px", borderBottom: `1px solid ${Bo}` }}>
            <p style={{ fontSize: "16px", fontWeight: 700, margin: 0, textAlign: "center" }}>Invitado {i + 1}:</p>
            <Fld><Lbl c={`Nombre del invitado ${i + 1}`} /><input style={{ ...ctrl, textAlign: "center" }} value={datosInv[i].nombre} onChange={e => setDatosInv(p => p.map((x, j) => j === i ? { ...x, nombre: e.target.value } : x))} />{errorTxt(`n${i}`)}</Fld>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              <Fld><Lbl c={`Tipo de documento ${i + 1}`} /><input style={{ ...ctrl, textAlign: "center", background: "#f9fafb" }} value="CEDULA" disabled /></Fld>
              <Fld><Lbl c={`Número de documento ${i + 1}`} /><input style={{ ...ctrl, textAlign: "center" }} inputMode="numeric" value={datosInv[i].documento} onChange={e => setDatosInv(p => p.map((x, j) => j === i ? { ...x, documento: e.target.value } : x))} />{errorTxt(`d${i}`)}</Fld>
            </div>
          </div>
        ))}
      </Box>}

      {permisosZona.adjuntarPdf === true && <Box>
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: "16px", fontWeight: 700, margin: "0 0 10px" }}>Información Adjuntada</p>
          <a href={String(permisosZona.urlPdf)} target="_blank" rel="noreferrer" style={{ display: "inline-block", padding: "12px 20px", background: R, color: "#fff", borderRadius: 10, textDecoration: "none", fontSize: "16px", fontWeight: 700 }}>Descargar</a>
        </div>
      </Box>}

      <Box title="Observaciones y recomendaciones">
        <p style={{ fontSize: "16px", margin: 0, whiteSpace: "pre-wrap" }}>{permisosZona.ObservacionesYRecomendaciones ?? ""}</p>
      </Box>

      <BotonRojo style={{ width: "100%", padding: "16px", marginBottom: "32px", fontSize: "18px" }} onClick={guardar}>✓ Guardar reserva</BotonRojo>
    </div>
  );
};

const ReservasWeb = () => {
  // Desde "Pendientes" → "Horarios" se llega con la zona ya elegida (como _verHorarios en la app).
  const { state } = useLocation();
  const [zona, setZona] = useState(() => state?.zonaId ? { zonaId: state.zonaId, titulo: zonaIdANombre[state.zonaId] ?? state.zonaId } : null);
  const [fecha, setFecha] = useState(null);
  const [version, setVersion] = useState(0);
  return (
    <div style={{ background: F, minHeight: "100vh" }}>
      <BarraSuperior />
      {!zona && <SelZona onSelect={setZona} />}
      {zona && !fecha && <Calendario key={version} zona={zona} onCrear={setFecha} onBack={() => setZona(null)} />}
      {zona && fecha && <Formulario zona={zona} fecha={fecha} onBack={() => setFecha(null)} onGuardado={() => { setFecha(null); setVersion(v => v + 1); }} />}
      <BotonSOS />
    </div>
  );
};

export default ReservasWeb;
